import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

// MUI components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// useUserContext to get idToken and email
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { useTranslation } from "react-i18next";

const WALLET_API_URL =
  process.env.REACT_APP_WALLET_API_URL ||
  "https://dev-pdapi.astrophant.online/api/qrcode/v1/bet/auth/wallet/";

// 自定义树节点样式
const TreeNode = styled(Box)(({ theme, level = 0 }) => ({
  display: "flex",
  flexDirection: "column",
  marginLeft: level * 16,
  paddingLeft: 8,
  borderLeft: "1px dashed",
  borderColor: theme.palette.divider,
}));

const TreeNodeHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "4px 0",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
});

// WalletInfo component
const WalletInfo = ({ wallet }) => {
  const { t } = useTranslation("common");
  if (!wallet) return null;
  return (
    <Box sx={{ ml: 2, mt: 1, p: 1, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
      <Typography variant="body2">
        <strong>{t("credit.balance")}</strong> {wallet.lsBalance} {wallet.lsCurrency}
      </Typography>
      <Typography variant="body2">
        <strong>{t("credit.posId")}</strong> {wallet.posPid} ({wallet.posCurrency})
      </Typography>
      <Typography variant="body2">
        <strong>{t("credit.minBet")}</strong> {wallet.posMinBet}
      </Typography>
    </Box>
  );
};

WalletInfo.propTypes = {
  wallet: PropTypes.shape({
    lsBalance: PropTypes.number,
    lsCurrency: PropTypes.string,
    posPid: PropTypes.string,
    posCurrency: PropTypes.string,
    posMinBet: PropTypes.number,
  }),
};

// 递归渲染树节点
const ThirdPartyTreeNode = ({ node, level = 0 }) => {
  const [expanded, setExpanded] = useState(level < 1); // 默认展开第一级

  return (
    <TreeNode level={level}>
      <TreeNodeHeader onClick={() => setExpanded(!expanded)}>
        <IconButton size="small" sx={{ mr: 1 }}>
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Box>
          <Typography component="span">{node.Label}</Typography>
          {node.Credit !== undefined && (
            <Typography variant="caption" color="textSecondary" display="block">
              Credit: {node.Credit}
            </Typography>
          )}
        </Box>
      </TreeNodeHeader>

      <Collapse in={expanded}>
        {node.Wallet && <WalletInfo wallet={node.Wallet} />}
        {node.Children?.map((child) => (
          <ThirdPartyTreeNode key={child.ID} node={child} level={level + 1} />
        ))}
      </Collapse>
    </TreeNode>
  );
};

ThirdPartyTreeNode.propTypes = {
  node: PropTypes.shape({
    ID: PropTypes.string.isRequired,
    Label: PropTypes.string.isRequired,
    Credit: PropTypes.number,
    Parent: PropTypes.string,
    Wallet: PropTypes.object,
    Children: PropTypes.array,
  }).isRequired,
  level: PropTypes.number,
};

ThirdPartyTreeNode.defaultProps = {
  level: 0,
};

function CreditsSP() {
  const { t } = useTranslation("common");
  const [hierarchyData, setHierarchyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [walletError, setWalletError] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const { idToken, userData, posPidArray } = useUserContext() || {};

  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const posPidList = useMemo(() => {
    if (!Array.isArray(posPidArray)) return [];
    return posPidArray
      .map((item) => (item?.pos_pid ? String(item.pos_pid).trim() : ""))
      .filter(Boolean);
  }, [posPidArray]);

  const fetchThirdPartiesHierarchy = async () => {
    if (!backendUrl) {
      setError("後端 API 位址未設定，請聯絡系統管理員。");
      setIsLoading(false);
      return;
    }
    if (!idToken) {
      setError("User is not authenticated.");
      setIsLoading(false);
      return;
    }
    if (!userData?.email) {
      setError("Email is required to fetch hierarchy data");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(backendUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: `
            query GetDolibarrWarehouseHierarchyByEmail($email: String!) {
              GetDolibarrWarehouseHierarchyByEmail(email: $email) {
                Username
                MatchedNode {
                  ID
                  Label
                  Parent
                  Credit
                  Children {
                    ID
                    Label
                    Parent
                    Credit
                    Children {
                      ID
                      Label
                      Parent
                      Credit
                      Children {
                        ID
                        Label
                        Parent
                        Credit
                        Children {
                          ID
                          Label
                          Parent
                          Credit
                          Children {
                            ID
                            Label
                            Parent
                            Credit
                            Children {
                              ID
                              Label
                              Parent
                              Credit
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            email: userData.email,
          },
        }),
      });

      const { data, errors } = await response.json();
      console.log("Credit hierarchy API response:", { data, errors });

      if (!response.ok) {
        const message = errors?.[0]?.message || `Server responded with status: ${response.status}`;
        throw new Error(message);
      }

      if (errors) {
        throw new Error(errors[0].message || "Error fetching third parties hierarchy");
      }

      setHierarchyData(data.GetDolibarrWarehouseHierarchyByEmail);
      setError(null);
      setIsLoading(false);
    } catch (err) {
      console.error("Error fetching third parties hierarchy:", err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchThirdPartiesHierarchy();
  }, [idToken, userData?.email, backendUrl]);

  useEffect(() => {
    const fetchWallets = async () => {
      setWalletLoading(true);
      if (!idToken) {
        setWalletError("User is not authenticated.");
        setWalletLoading(false);
        return;
      }
      try {
        const url = new URL(WALLET_API_URL);
        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
        if (!response.ok) throw new Error(`Server responded with status: ${response.status}`);
        const data = await response.json();
        console.log("Wallet API response:", data);
        setWallets(Array.isArray(data.result) ? data.result : []);
        setWalletError(null);
      } catch (fetchError) {
        console.error("Error fetching wallet data:", fetchError);
        setWalletError(fetchError.message);
        setWallets([]);
      } finally {
        setWalletLoading(false);
      }
    };

    fetchWallets();
  }, [idToken, posPidList]);

  const walletSection = (() => {
    if (walletLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (walletError) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="error">{walletError}</Typography>
        </Box>
      );
    }
    if (!wallets.length) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography>No wallets found</Typography>
        </Box>
      );
    }
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        {wallets.map((wallet) => (
          <Box
            key={`${wallet.pos_pid}-${wallet.ls_currency}`}
            p={1.5}
            border="1px solid #e0e0e0"
            borderRadius={1}
          >
            <Typography variant="subtitle2">{wallet.pos_pid}</Typography>
            <Typography variant="body2" color="text.secondary">
              {wallet.ls_balance} {wallet.ls_currency || "USD"}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  })();

  const renderHierarchyBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }
    if (!hierarchyData) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Typography>No hierarchy data found</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ maxHeight: { xs: "55vh", lg: "60vh" }, overflow: "auto", px: 2, py: 1 }}>
        <ThirdPartyTreeNode node={hierarchyData.MatchedNode} />
      </Box>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  {t("pageTitles.credit")}
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Grid
                  container
                  spacing={2}
                  direction={{ xs: "column", lg: "row" }}
                  alignItems="stretch"
                >
                  <Grid item xs={12} lg={8} display="flex">
                    <Card
                      variant="outlined"
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                      }}
                    >
                      <MDBox
                        px={2}
                        py={1.5}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          background:
                            "linear-gradient(135deg, rgba(3,155,229,0.15) 0%, rgba(3,169,244,0.05) 100%)",
                        }}
                      >
                        <Icon fontSize="small" color="primary">
                          account_tree
                        </Icon>
                        <Typography variant="subtitle2" color="text.primary">
                          {t("pageTitles.credit")}
                        </Typography>
                      </MDBox>
                      {renderHierarchyBody()}
                    </Card>
                  </Grid>
                  {/* <Grid item xs={12} lg={4} display="flex">
                    <Card
                      variant="outlined"
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                      }}
                    >
                      <MDBox p={2}>
                        <Typography variant="subtitle1" gutterBottom color="text.primary">
                          <Box component="span" display="inline-flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" color="primary">
                              account_balance_wallet
                            </Icon>
                            {t("credit.walletBalances")}
                          </Box>
                        </Typography>
                        <MDBox mt={1}>{walletSection}</MDBox>
                      </MDBox>
                    </Card>
                  </Grid> */}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default CreditsSP;
